import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Chip,
  Button, CircularProgress, Divider, Tabs, Tab,
  Collapse, IconButton, TextField, InputAdornment,
} from '@mui/material';
import {
  ExpandMore, ExpandLess, PlayCircle, Article,
  Search, School, VideoLibrary, OpenInNew,
} from '@mui/icons-material';
import { studyAPI } from '../services/api';

const CATEGORY_COLORS = {
  'Cosmology':          '#4fc3f7',
  'Astrophysics':       '#ffa726',
  'Theoretical Physics':'#ab47bc',
  'Astrobiology':       '#66bb6a',
  'Quantum Physics':    '#ef5350',
  'General':            '#90caf9',
};

// ─── Theory Card ──────────────────────────────────────────────────────────────
function TheoryCard({ theory }) {
  const [expanded, setExpanded] = useState(false);
  const color = CATEGORY_COLORS[theory.category] || '#4fc3f7';

  return (
    <Card sx={{
      border: `1px solid ${color}33`,
      transition: 'all 0.3s',
      position: 'relative', overflow: 'hidden',
      '&::before': {
        content: '""', position: 'absolute',
        top: 0, left: 0, right: 0, height: 3,
        bgcolor: color,
      },
      '&:hover': { border: `1px solid ${color}66`, boxShadow: `0 4px 20px ${color}15` },
    }}>
      <CardContent sx={{ p: 3 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
          <Typography sx={{ fontSize: 36, flexShrink: 0 }}>{theory.icon}</Typography>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary' }}>
                {theory.title}
              </Typography>
              <Chip label={theory.category} size="small"
                sx={{ bgcolor: `${color}22`, color, border: `1px solid ${color}44`, fontWeight: 700, fontSize: 10 }} />
            </Box>
            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
              {theory.shortDescription}
            </Typography>
          </Box>
        </Box>

        {/* Papers count */}
        {theory.papers?.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Chip
              icon={<Article sx={{ fontSize: '14px !important' }} />}
              label={`${theory.papers.length} Research Paper${theory.papers.length > 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'primary.light',
                border: '1px solid rgba(255,255,255,0.2)', fontSize: 10 }}
            />
          </Box>
        )}

        {/* Read More button */}
        <Button
          onClick={() => setExpanded(e => !e)}
          endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          sx={{
            color, textTransform: 'none', fontWeight: 700, px: 0,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
        >
          {expanded ? 'Show Less' : 'Read More'}
        </Button>

        {/* Expanded content */}
        <Collapse in={expanded} timeout={300}>
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ borderColor: `${color}22`, mb: 2 }} />

            {/* Full description */}
            <Typography variant="body2" sx={{
              color: 'text.secondary', lineHeight: 1.9,
              whiteSpace: 'pre-line', mb: 3,
            }}>
              {theory.fullDescription}
            </Typography>

            {/* Research Papers */}
            {theory.papers?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{
                  fontWeight: 800, color, mb: 1.5,
                  textTransform: 'uppercase', letterSpacing: 1, fontSize: 11,
                }}>
                  📄 Research Papers
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {theory.papers.map((paper, i) => (
                    <Box key={i} sx={{
                      p: 2, borderRadius: 2,
                      bgcolor: `${color}08`,
                      border: `1px solid ${color}22`,
                      display: 'flex', alignItems: 'flex-start',
                      justifyContent: 'space-between', gap: 2,
                      flexWrap: 'wrap',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: `${color}14`, border: `1px solid ${color}44` },
                    }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.3 }}>
                          {paper.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {paper.author && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              ✍️ {paper.author}
                            </Typography>
                          )}
                          {paper.journal && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              • {paper.journal}
                            </Typography>
                          )}
                          {paper.year && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              • {paper.year}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      <Button
                        size="small" variant="outlined"
                        endIcon={<OpenInNew sx={{ fontSize: 13 }} />}
                        href={paper.pdfUrl} target="_blank" rel="noopener noreferrer"
                        sx={{
                          borderColor: `${color}66`, color,
                          fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                          '&:hover': { borderColor: color, bgcolor: `${color}15` },
                        }}
                      >
                        View PDF
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

// ─── Video Card ───────────────────────────────────────────────────────────────
function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);
  const color = CATEGORY_COLORS[video.category] || '#4fc3f7';

  return (
    <Card sx={{
      border: `1px solid ${color}33`, overflow: 'hidden',
      transition: 'all 0.2s',
      '&:hover': { border: `1px solid ${color}66`, transform: 'translateY(-3px)',
        boxShadow: `0 8px 24px ${color}18` },
    }}>
      {/* Thumbnail / Player */}
      <Box sx={{ position: 'relative', paddingTop: '56.25%', bgcolor: '#000', overflow: 'hidden' }}>
        {playing ? (
          <Box component="iframe"
            src={`${video.embedUrl}?autoplay=1&rel=0`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
          />
        ) : (
          <>
            <Box
              component="img"
              src={video.thumbnailUrl}
              alt={video.title}
              onError={e => { e.target.src = `https://img.youtube.com/vi/${video.embedUrl.split('/embed/')[1]}/hqdefault.jpg`; }}
              sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
            {/* Play overlay */}
            <Box
              onClick={() => setPlaying(true)}
              sx={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: 'rgba(0,0,0,0.35)', cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
              }}
            >
              <Box sx={{
                width: 56, height: 56, borderRadius: '50%',
                bgcolor: 'rgba(255,0,0,0.85)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                transition: 'transform 0.2s',
                '&:hover': { transform: 'scale(1.1)' },
              }}>
                <PlayCircle sx={{ fontSize: 36, color: '#fff' }} />
              </Box>
            </Box>
            {/* Duration badge */}
            {video.duration && (
              <Chip label={video.duration} size="small"
                sx={{ position: 'absolute', bottom: 8, right: 8,
                  bgcolor: 'rgba(0,0,0,0.8)', color: '#fff',
                  fontSize: 10, fontWeight: 700, height: 20 }} />
            )}
          </>
        )}
      </Box>

      <CardContent sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5, lineHeight: 1.4 }}>
          {video.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {video.channel && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              📺 {video.channel}
            </Typography>
          )}
          <Chip label={video.category} size="small"
            sx={{ bgcolor: `${color}18`, color, fontSize: 9, fontWeight: 700, height: 16 }} />
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Theory Section ───────────────────────────────────────────────────────────
function TheorySection() {
  const [theories, setTheories]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [category, setCategory]   = useState('All');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await studyAPI.getTheories();
      setTheories(res.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categories = ['All', ...new Set(theories.map(t => t.category))];

  const filtered = theories.filter(t => {
    const matchSearch = !search ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.shortDescription.toLowerCase().includes(search.toLowerCase());
    const matchCat = category === 'All' || t.category === category;
    return matchSearch && matchCat;
  });

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search theories..."
          value={search} onChange={e => setSearch(e.target.value)}
          size="small" sx={{ minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color:'text.secondary', fontSize:18 }} /></InputAdornment> }}
        />
        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
          {categories.map(cat => {
            const color = CATEGORY_COLORS[cat] || '#4fc3f7';
            return (
              <Chip key={cat} label={cat}
                onClick={() => setCategory(cat)}
                sx={{
                  cursor: 'pointer', fontWeight: 700,
                  bgcolor: category === cat ? `${color}22` : 'transparent',
                  color:   category === cat ? color : 'text.secondary',
                  border:  category === cat ? `1px solid ${color}` : '1px solid rgba(255,255,255,0.1)',
                }}
              />
            );
          })}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      ) : (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Showing <strong style={{ color: '#4fc3f7' }}>{filtered.length}</strong> of {theories.length} theories
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filtered.map(theory => (
              <TheoryCard key={theory.id} theory={theory} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

// ─── Video Section ────────────────────────────────────────────────────────────
function VideoSection() {
  const [videos, setVideos]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [category, setCategory] = useState('All');

  useEffect(() => {
    studyAPI.getVideos()
      .then(res => setVideos(res.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const categories = ['All', ...new Set(videos.map(v => v.category))];
  const filtered   = category === 'All' ? videos : videos.filter(v => v.category === category);

  return (
    <Box>
      {/* Category filter */}
      <Box sx={{ display: 'flex', gap: 0.8, mb: 3, flexWrap: 'wrap' }}>
        {categories.map(cat => {
          const color = CATEGORY_COLORS[cat] || '#4fc3f7';
          return (
            <Chip key={cat} label={cat}
              onClick={() => setCategory(cat)}
              sx={{
                cursor: 'pointer', fontWeight: 700,
                bgcolor: category === cat ? `${color}22` : 'transparent',
                color:   category === cat ? color : 'text.secondary',
                border:  category === cat ? `1px solid ${color}` : '1px solid rgba(255,255,255,0.1)',
              }}
            />
          );
        })}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      ) : (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {filtered.length} video{filtered.length !== 1 ? 's' : ''} available
          </Typography>
          <Grid container spacing={2}>
            {filtered.map(video => (
              <Grid item xs={12} sm={6} md={4} key={video.id}>
                <VideoCard video={video} />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
}

// ─── Main StudyZonePage ───────────────────────────────────────────────────────
export default function StudyZonePage() {
  const [tab, setTab] = useState(0);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            🎓 Study Zone
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            Explore space theories, research papers and educational videos
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Chip label="6 Theories" size="small"
            sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'primary.light', fontWeight: 700 }} />
          <Chip label="8 Videos" size="small"
            sx={{ bgcolor: 'rgba(102,187,106,0.1)', color: '#66bb6a', fontWeight: 700 }} />
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3,
        '& .MuiTab-root': { color: 'text.secondary', fontWeight: 600 },
        '& .Mui-selected': { color: 'primary.light' },
        '& .MuiTabs-indicator': { bgcolor: 'primary.main' } }}>
        <Tab icon={<School />}       label="Theories & Papers" iconPosition="start" />
        <Tab icon={<VideoLibrary />} label="Video Lectures"    iconPosition="start" />
      </Tabs>

      {tab === 0 && <TheorySection />}
      {tab === 1 && <VideoSection  />}
    </Box>
  );
}